import type {ConnectionOperation, EntityOperation} from '@github-ui/optimistic-connection'
import {DOCUMENT_FIELDS_PATH} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {settleConfirmedOperation} from '@github-ui/optimistic-connection/settleConfirmedOperation'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useConnectionMutation} from '@github-ui/optimistic-connection/useConnectionMutation'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import type {AddBlockedByMutationMutation} from '@github-ui/server-persisted-queries/types/addBlockedByMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

import {issueViewerSecondaryViewQueryKey} from '../components/fetch-issue-viewer-secondary-view'
import {
  BLOCKED_BY_CONNECTION_PATH,
  BLOCKING_CONNECTION_PATH,
} from '../components/sections/relations-section/dependency-issue-query-types'
import {dependenciesPickerRelationshipsQueryKey} from '../components/sections/relations-section/fetch-dependencies-picker-relationships'
import {escapeOptimisticTitleHTML} from '../utils/helpers'

export type OptimisticDependencyIssue = {
  id: string
  title: string
  url: string
  number: number
  state: string
  stateReason?: string | null
  repository: {id: string; nameWithOwner: string}
}

type DependencyDirection = 'blockedBy' | 'blocking'
type AddBlockedByVariables = {issueId: string; nameWithOwner: string; issueNumber: number}
type AddBlockedByOperation = Extract<ConnectionOperation, {type: 'addToConnection'}>
type CountPublication = {
  resolve: (operation: EntityOperation) => void
  reject: (error: Error) => void
}
type MutationContext = {countPublication: CountPublication}
type MutationOptions = Omit<
  Parameters<typeof useConnectionMutation<AddBlockedByMutationMutation, Error, MutationContext>>[0],
  'ownerId' | 'connectionPath'
>

type CountMutationVariables = {
  operation: EntityOperation
  settlement: Promise<EntityOperation>
}

export function useDependencyCountMutation() {
  const {mutate} = useMutation<EntityOperation, Error, CountMutationVariables>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: variables => variables.settlement,
    onSuccess: operation => notifySettlement(operation, ENTITY_FIELDS_OWNER, DOCUMENT_FIELDS_PATH),
  })

  return (operation: EntityOperation): CountPublication => {
    const settlement = Promise.withResolvers<EntityOperation>()
    mutate({operation, settlement: settlement.promise})
    return {resolve: settlement.resolve, reject: settlement.reject}
  }
}

function connectionPath(direction: DependencyDirection) {
  return direction === 'blockedBy' ? BLOCKED_BY_CONNECTION_PATH : BLOCKING_CONNECTION_PATH
}

function directionFor(operation: AddBlockedByOperation): DependencyDirection {
  return operation.connectionPath[0] === 'topBlocking' ? 'blocking' : 'blockedBy'
}

function toOptimisticOperation(
  dependencyIssue: OptimisticDependencyIssue,
  direction: DependencyDirection,
): AddBlockedByOperation {
  const [owner, name] = dependencyIssue.repository.nameWithOwner.split('/')
  return {
    type: 'addToConnection',
    connectionPath: connectionPath(direction),
    anchor: {at: 'end'},
    counts: [{path: ['issueDependenciesSummary', direction], delta: 1}],
    node: {
      ...dependencyIssue,
      titleHTML: escapeOptimisticTitleHTML(dependencyIssue.title),
      repository: {...dependencyIssue.repository, name, owner: {login: owner}},
    },
  }
}

async function addBlockedByRequest({
  nameWithOwner,
  issueNumber,
  dependencyIssueId,
  direction,
}: AddBlockedByVariables & {dependencyIssueId: string; direction: DependencyDirection}) {
  const relationship = direction === 'blockedBy' ? 'blocked_by' : 'blocking'
  const body = direction === 'blockedBy' ? {blockingIssueId: dependencyIssueId} : {issueId: dependencyIssueId}
  const response = await reactFetchJSON(`/${nameWithOwner}/issues/${issueNumber}/relationships/${relationship}.json`, {
    method: 'POST',
    body,
  })
  const result = (await response.json()) as {
    data?: AddBlockedByMutationMutation | null
    errors?: ReadonlyArray<{message?: string}> | null
  }
  const error = result.errors?.find(item => item.message)?.message
  if (!response.ok || error || !result.data?.addBlockedBy?.issue || !result.data.addBlockedBy.blockingIssue) {
    throw new Error(error ?? 'Failed to add dependency')
  }
  return result.data
}

export function useAddBlockedByMutation({issueId, nameWithOwner, issueNumber}: AddBlockedByVariables) {
  const queryClient = useQueryClient()
  const [owner, repo] = nameWithOwner.split('/')
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner ?? '', repo ?? '', issueNumber)
  const pickerQueryKey = dependenciesPickerRelationshipsQueryKey(nameWithOwner, issueNumber)
  const publishDependencyCount = useDependencyCountMutation()

  const mutationOptions = {
    mutationFn: operation => {
      const addOperation = operation as AddBlockedByOperation
      return addBlockedByRequest({
        nameWithOwner,
        issueNumber,
        issueId,
        dependencyIssueId: addOperation.node.id,
        direction: directionFor(addOperation),
      })
    },
    onMutate: ({operation}) => {
      const addOperation = operation as AddBlockedByOperation
      const direction = directionFor(addOperation)
      const countOperation: EntityOperation = {
        type: 'deltaEntityFieldValues',
        id: direction === 'blockedBy' ? issueId : addOperation.node.id,
        field: 'issueDependenciesSummary',
        deltas: {blockedBy: 1},
      }
      return {
        countPublication: publishDependencyCount(countOperation),
      }
    },
    onSuccess: async (data, variables, context) => {
      const payload = data.addBlockedBy
      const addOperation = variables.operation as AddBlockedByOperation
      const direction = directionFor(addOperation)
      const currentIssue = direction === 'blockedBy' ? payload?.issue : payload?.blockingIssue
      if (currentIssue) {
        await settleConfirmedOperation({
          queryClient,
          targets: [{queryKey: secondaryQueryKey}],
          ownerId: issueId,
          operation: {
            type: 'setFields',
            id: issueId,
            fields: {
              topBlockedBy: currentIssue.topBlockedBy,
              topBlocking: currentIssue.topBlocking,
              issueDependenciesSummary: currentIssue.issueDependenciesSummary,
            },
          },
        })
      }
      await queryClient.invalidateQueries({queryKey: pickerQueryKey})
      const blockedByCount = payload?.issue?.issueDependenciesSummary.blockedBy
      if (context?.countPublication && typeof blockedByCount === 'number' && payload?.issue) {
        context.countPublication.resolve({
          type: 'setFields',
          id: payload.issue.id,
          fields: {issueDependenciesSummary: payload.issue.issueDependenciesSummary},
        })
      }
    },
    onError: (error, _variables, context) => {
      context?.countPublication?.reject(error)
    },
  } satisfies MutationOptions

  const {mutateAsync: mutateBlockedBy} = useConnectionMutation<AddBlockedByMutationMutation, Error, MutationContext>({
    ...mutationOptions,
    ownerId: issueId,
    connectionPath: BLOCKED_BY_CONNECTION_PATH,
  })
  const {mutateAsync: mutateBlocking} = useConnectionMutation<AddBlockedByMutationMutation, Error, MutationContext>({
    ...mutationOptions,
    ownerId: issueId,
    connectionPath: BLOCKING_CONNECTION_PATH,
  })

  return useCallback(
    (dependencyIssue: OptimisticDependencyIssue, direction: DependencyDirection) =>
      (direction === 'blockedBy' ? mutateBlockedBy : mutateBlocking)({
        operation: toOptimisticOperation(dependencyIssue, direction),
      }),
    [mutateBlockedBy, mutateBlocking],
  )
}
